/* T8 Contact hero ticker — Framer-style curved marquee.
   The track scrolls continuously; every frame each tile's visible height is set from its distance to the centre of the
   ticker: 1:1 square (min height) in the middle, growing to the max height at the left and right edges, which reads as a
   concave 3D arc. Tiles are full max height and cropped with clip-path (compositor-friendly, the image never resizes).
   Heights scale down proportionally when the hero has less room than the max height. Pauses off-screen and on hover. */
(function () {
  'use strict';

  function init(root) {
    (root || document).querySelectorAll('[data-t8-ticker]').forEach(function (tk) {
      if (tk.dataset.ready) return; tk.dataset.ready = '1';
      var track = tk.querySelector('[data-t8-ticker-track]');
      if (!track) return;
      var originals = Array.prototype.slice.call(track.children);
      if (!originals.length) return;

      var MIN = parseFloat(tk.dataset.min) || 342, MAX = parseFloat(tk.dataset.max) || 432, GAP = parseFloat(tk.dataset.gap) || 16;
      var dir = tk.dataset.direction === 'left' ? -1 : 1;           // 1 = tiles travel left → right
      var secs = parseFloat(tk.dataset.speed) || 6;                   // seconds for one tile to pass
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var tiles = [], setW = 0, offset = 0, last = 0, running = true, visible = true, hover = false;
      var maxH = MAX, minH = MIN, tileW = MIN;

      function measure() {
        var avail = tk.clientHeight - 2 * GAP;                       // keep a gap of breathing room above and below
        var scale = Math.min(1, avail / MAX, (tk.clientWidth * .42) / MIN);   // fit the band height, and on narrow screens show at least ~2.4 tiles
        scale = Math.max(.3, scale);
        maxH = MAX * scale; minH = MIN * scale; tileW = minH;        // centre tile is square
        tk.style.setProperty('--tk-h', maxH + 'px');
        tk.style.setProperty('--tk-w', tileW + 'px');
        tk.style.setProperty('--tk-gap', GAP + 'px');
        // enough copies of the set to cover the ticker twice, so the loop never shows a gap
        setW = originals.length * (tileW + GAP);
        var need = Math.ceil((tk.clientWidth * 2) / setW) + 1;
        while (track.children.length < originals.length * need) {
          originals.forEach(function (o) { var c = o.cloneNode(true); c.setAttribute('aria-hidden', 'true'); c.setAttribute('tabindex', '-1'); c.querySelectorAll('a').forEach(function (a) { a.setAttribute('tabindex', '-1'); }); track.appendChild(c); });
        }
        tiles = Array.prototype.slice.call(track.children);
        band();
      }

      // clip path for a tile whose left edge sits at dx0 from the centre: top and bottom edges sampled along the curve,
      // 12px rounded corners. Recomputed every frame, so the curve of each image changes as it travels.
      var R = 12, S = 8;
      function shape(dx0, cx) {
        var W = tileW, H = maxH, top = function (x) { return (H - heightAt(dx0 + x, cx)) / 2; }, d = [], k, x;
        var r = Math.min(R, W / 2);
        d.push('M' + r.toFixed(1) + ' ' + top(r).toFixed(1));
        for (k = 1; k <= S; k++) { x = r + (W - 2 * r) * k / S; d.push('L' + x.toFixed(1) + ' ' + top(x).toFixed(1)); }
        d.push('Q' + W.toFixed(1) + ' ' + top(W).toFixed(1) + ' ' + W.toFixed(1) + ' ' + (top(W) + r).toFixed(1));
        d.push('L' + W.toFixed(1) + ' ' + (H - top(W) - r).toFixed(1));
        d.push('Q' + W.toFixed(1) + ' ' + (H - top(W)).toFixed(1) + ' ' + (W - r).toFixed(1) + ' ' + (H - top(W - r)).toFixed(1));
        for (k = S - 1; k >= 0; k--) { x = r + (W - 2 * r) * k / S; d.push('L' + x.toFixed(1) + ' ' + (H - top(x)).toFixed(1)); }
        d.push('Q0 ' + (H - top(0)).toFixed(1) + ' 0 ' + (H - top(0) - r).toFixed(1));
        d.push('L0 ' + (top(0) + r).toFixed(1));
        d.push('Q0 ' + top(0).toFixed(1) + ' ' + r.toFixed(1) + ' ' + top(r).toFixed(1) + 'Z');
        return 'path("' + d.join(' ') + '")';
      }

      function heightAt(dx, cx) { var t = Math.min(1, Math.abs(dx) / cx); return minH + (maxH - minH) * Math.pow(t, 1.6); }

      // the cream band follows the same curve as the tiles, with an equal GAP of padding above and below every tile:
      // its edge is the tile curve offset by GAP
      function band() {
        var w = tk.clientWidth, H = tk.clientHeight, cx = w / 2, cy = H / 2, top = [], bot = [], N = 48;
        for (var k = 0; k <= N; k++) {
          var x = w * k / N, half = heightAt(x - cx, cx) / 2 + GAP;   // tile edges follow the same curve, so the padding is exactly GAP everywhere
          top.push(x.toFixed(1) + 'px ' + Math.max(0, cy - half).toFixed(1) + 'px');
          bot.unshift(x.toFixed(1) + 'px ' + Math.min(H, cy + half).toFixed(1) + 'px');
        }
        tk.style.clipPath = 'polygon(' + top.concat(bot).join(',') + ')';
      }

      function paint() {
        var w = tk.clientWidth, cx = w / 2;
        track.style.transform = 'translate3d(' + (offset - setW) + 'px,0,0)';
        for (var i = 0; i < tiles.length; i++) {
          var x = (offset - setW) + i * (tileW + GAP) + tileW / 2;  // tile centre in ticker coordinates
          if (x < -tileW || x > w + tileW) continue;
          tiles[i].style.clipPath = shape(x - tileW / 2 - cx, cx);    // top + bottom edges follow the curve across the tile's width
        }
      }

      function frame(now) {
        if (!last) last = now;
        var dt = Math.min(64, now - last); last = now;
        if (running && visible && !hover && !reduce) {
          offset += dir * (tileW + GAP) / (secs * 1000) * dt;
          if (offset >= setW) offset -= setW;
          if (offset < 0) offset += setW;
        }
        paint();
        if (visible) requestAnimationFrame(frame); else last = 0;
      }

      measure(); paint();
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (en) {
          var was = visible; visible = en[0].isIntersecting;
          if (visible && !was) requestAnimationFrame(frame);
        }).observe(tk);
      }
      tk.addEventListener('mouseenter', function () { hover = true; });
      tk.addEventListener('mouseleave', function () { hover = false; });
      // re-measure whenever the ticker's box changes (initial layout settling, fonts, resize, editor changes)
      var lastH = -1, lastW = -1;
      function remeasure() { if (tk.clientHeight === lastH && tk.clientWidth === lastW) return; lastH = tk.clientHeight; lastW = tk.clientWidth; measure(); paint(); }
      if ('ResizeObserver' in window) new ResizeObserver(remeasure).observe(tk);
      else { var rt; window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(remeasure, 120); }); }
      window.addEventListener('load', remeasure);
      requestAnimationFrame(frame);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
